import { ResidentialIncentiveApiData } from '../../../types/ResidentialIncentivesDataType';
import {
    selectedResidentialIncentiveData,
    zipCode,
    filteredResidentialIncentivesData,
} from '../../../state/GlobalState';
import { useAtom } from 'jotai';
import { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { addPage, generatePdf, initPdf, isDocumentReady } from '../../../services/PdfService';
import PDFReportPageResidential from './PDFReportPageResidential';

const PDFReportDocumentResidential = () => {
    const [zipcode] = useAtom<string>(zipCode);
    const [selectedIncentives] = useAtom<ResidentialIncentiveApiData[]>(
        selectedResidentialIncentiveData,
    );
    const [filteredIncentives] = useAtom<ResidentialIncentiveApiData[]>(
        filteredResidentialIncentivesData,
    );
    const incentivesToBeConsidered = useMemo(() => {
        return selectedIncentives.length === 0 ? filteredIncentives : selectedIncentives;
    }, [selectedIncentives, filteredIncentives]);
    const [pagedIncentives, setPagedIncentives] = useState<ResidentialIncentiveApiData[][]>([]);
    const reportRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const INCENTIVES_PER_PAGE = 8;
        const TOTAL_PAGES = Math.ceil(incentivesToBeConsidered.length / INCENTIVES_PER_PAGE);
        const pagesArray = Array(TOTAL_PAGES);
        incentivesToBeConsidered.forEach((incentive, key) => {
            const index = Math.floor(key / INCENTIVES_PER_PAGE);
            if (!pagesArray[index]) {
                pagesArray[index] = [];
            }
            pagesArray[index].push(incentive);
        });

        setPagedIncentives(pagesArray);
    }, [incentivesToBeConsidered]);

    const generatePDF = async (
        zipcode: string,
        pagedIncentives: ResidentialIncentiveApiData[][],
    ) => {
        const reportPages = document.querySelectorAll('.report-page');
        if (reportPages.length === pagedIncentives.length) {
            console.log('pdf generation: ', pagedIncentives.length, ' pages');
            initPdf(pagedIncentives.length);
            reportPages.forEach((page, index) => addPage(page as HTMLElement, index));
            if (isDocumentReady()) {
                try {
                    await generatePdf(`residential-incentives-${zipcode}-report.pdf`);
                    console.log('PDF generation complete');
                } catch (error) {
                    console.error('PDF generation error:', error);
                }
            }
        }
    };

    const orchestrateReportPagesObserver = useCallback(() => {
        const observer = new MutationObserver(() => generatePDF(zipcode, pagedIncentives));
        if (reportRef.current) {
            observer.observe(reportRef.current, { childList: true, subtree: true });
        }
        return () => observer.disconnect();
    }, [zipcode, pagedIncentives]);

    useEffect(() => {
        return orchestrateReportPagesObserver();
    }, [orchestrateReportPagesObserver]);

    return (
        <div ref={reportRef}>
            {pagedIncentives.map((incentives, index) => (
                <div data-testid="report-page" className="report-page" key={index}>
                    <PDFReportPageResidential zipcode={zipcode} incentives={incentives} />
                </div>
            ))}
        </div>
    );
};

export default PDFReportDocumentResidential;
